import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaChartBar, FaFileAlt, FaSpinner } from 'react-icons/fa';
import HeaderCard from '../../components/Header/HeaderCard';
import SecondarySidebar from '../../components/SideBar/SecondarySidebar';
import Recapitulatif from './Etats/Recapitulatif';
import { apiService } from '../../components/Config/api.service';

const FinancialStatements = () => {
  const [activeTab, setActiveTab] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totals, setTotals] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [id, setId] = useState(null);
  // Small UX states
  const [showDesc, setShowDesc] = useState(true);
  const [lastGenerated, setLastGenerated] = useState(null);

  // Local date formatter to YYYY-MM-DD (no timezone shift)
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const setRange = (fromDate, toDate) => {
    setStartDate(fmt(fromDate));
    setEndDate(fmt(toDate));
  };
  const presets = {
    thisMonth: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0));
    },
    lastMonth: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0));
    },
    thisYear: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31));
    },
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await apiService.getElement('/reports');
        const data = response.data;

        if (data.status === 'success') {
          const filteredItems = data.data
            .sort((a, b) => {
              const aHasEtat = a.name.toLowerCase().includes('etat');
              const bHasEtat = b.name.toLowerCase().includes('etat');
              if (aHasEtat && !bHasEtat) return 1; 
              if (!aHasEtat && bHasEtat) return -1;
              return 0;
            })
            .map((report) => ({
              id: report.id,
              label: report.name,
              report_name: report.report_name,
              hasExcel: report.has_excel,
              description: report.description, 
            }));

          setMenuItems(filteredItems);
          setActiveTab(filteredItems[0]?.id || null); 
        } else {
          throw new Error(data.message || 'Failed to fetch reports');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  useEffect(() => {
    if (activeTab && startDate && endDate) {
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert('Veuillez sélectionner une période.');
      return;
    }

    const selectedMenuName =
      menuItems.find((item) => item.id === activeTab)?.report_name || 'Récapitulatif';
    setReportLoading(true);

    try {
      const response = await apiService.saveElement('/generate-report', {
        date_from: startDate,
        date_to: endDate,
        menu_name: selectedMenuName,
      });
  
      if (response.data.result.status === 'success') {
        setId(response.data.result.report_id); 
        setTotals(response.data.result.totals); 
        setPaymentDetails(response.data.result.payment_details || {});
        console.log('Report generated successfully:', response.data.result.payment_details);
        setLastGenerated({ startDate, endDate, activeTab });
      } else {
        throw new Error(response.data.message || 'Erreur lors de la génération du rapport.');
      }
    } catch (err) {
      console.error('Erreur lors de la génération du rapport:', err);
      setError('Erreur lors de la génération du rapport');
    } finally {
      setReportLoading(false);
    }
  };

  const renderContent = () => {
    if (!activeTab) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-gray-100 rounded-full p-4 mb-4">
            <FaFileAlt className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun rapport sélectionné</h3>
          <p className="text-gray-500">Veuillez sélectionner un rapport dans la barre latérale pour voir les détails.</p>
        </div>
      );
    }

    const selectedMenu = menuItems.find((item) => item.id === activeTab);
    const selectedMenuName = selectedMenu?.label || 'Récapitulatif';
    const hasExcel = selectedMenu?.hasExcel || false;
    const description = selectedMenu?.description || 'Description non disponible';

    return (
      <div className="space-y-4">
        {/* Récapitulatif Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200" aria-live="polite">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-[--bg-color] rounded-full p-2">
                <FaChartBar className="text-[--color-primary]" size={16} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Récapitulatif</h3>
            </div>
          </div>
          <div className="p-6">
            {reportLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-24 bg-gray-200 rounded" />
              </div>
            ) : (
              <Recapitulatif
                selectedMenuName={selectedMenuName}
                totals={totals}
                id={id}
                hasExcel={hasExcel}
                paymentDetails={paymentDetails}
                setPaymentDetails={setPaymentDetails}
                dateFrom={startDate}
                dateTo={endDate}
              />
            )}
          </div>
        </div>

        {/* Description Card (collapsible) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[--bg-color] rounded-full p-2">
                <FaFileAlt className="text-[--color-primary]" size={16} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Description du rapport</h3>
            </div>
            <button
              onClick={() => setShowDesc((v) => !v)}
              className="text-sm text-[--color-primary] hover:underline"
              aria-expanded={showDesc}
            >
              {showDesc ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          {showDesc && (
            <div className="p-6">
              <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          )}
        </div>
      </div>
    );
  };

  // if (loading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <div className="bg-[--bg-color] rounded-full p-4 mb-4 inline-block">
  //           <FaSpinner className="text-[--color-primary] animate-spin text-2xl" />
  //         </div>
  //         <p className="text-gray-600">Chargement des rapports...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="bg-red-100 rounded-full p-4 mb-4 inline-block">
            <FaFileAlt className="text-red-600 text-2xl" />
          </div>
          <p className="text-red-600 font-medium">Erreur: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <HeaderCard
        title="États Financiers"
        text="Générez et consultez vos rapports financiers et déclaratifs pour une période donnée."
      />

      {/* Date Selection Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-[--bg-color] rounded-full p-2">
              <FaCalendarAlt className="text-[--color-primary]" size={16} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Sélection de période</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.thisMonth}>
              Ce mois
            </button>
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.lastMonth}>
              Dernier mois
            </button>
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.thisYear}>
              Cette année
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            <div className="flex-1 space-y-4 sm:space-y-0 sm:flex sm:gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStartDate(v);
                    // Ensure end date stays >= start date
                    if (endDate && v && endDate < v) setEndDate('');
                  }}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                  max={endDate || undefined}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                />
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                onClick={handleGenerateReport}
                disabled={
                  !startDate ||
                  !endDate ||
                  reportLoading ||
                  (lastGenerated &&
                    lastGenerated.startDate === startDate &&
                    lastGenerated.endDate === endDate &&
                    lastGenerated.activeTab === activeTab)
                }
              >
                {reportLoading ? (
                  <>
                    <FaSpinner className="animate-spin" size={16} />
                    Génération...
                  </>
                ) : (
                  <>
                    <FaChartBar size={16} />
                    Générer les États
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Selected period summary + clear */}
          {startDate && endDate ? (
            <div className="text-sm text-gray-700 flex items-center gap-3">
              <span className="px-2 py-1 bg-gray-100 rounded">
                Période: {startDate} → {endDate}
              </span>
              <button
                className="text-xs text-red-600 hover:underline"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Réinitialiser
              </button>
            </div>
          ) : null}

          {!startDate || !endDate ? (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                ⚠️ Veuillez sélectionner une date de début et une date de fin pour générer les rapports.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4">
        {/* Sidebar (sticky) */}
        <div className="w-80 flex-shrink-0 sticky top-4 self-start">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Types de rapports</h3>
              <p className="text-sm text-gray-600 mt-1">Choisissez le rapport à générer</p>
            </div>
            <div className="p-2">
              <SecondarySidebar
                menuItems={menuItems}
                setActiveTab={setActiveTab}
                activeTab={activeTab}
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">{renderContent()}</div>
      </div>
    </div>
  );
};

export default FinancialStatements;